(function (root, factory) {
  const createPageTrackingElementExposure = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingElementExposure;
    return;
  }

  if (root) {
    root.createPageTrackingElementExposure = createPageTrackingElementExposure;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingElementExposureFactory() {
  return function createPageTrackingElementExposure(core, options) {
    const currentOptions = options || {};
    const pageIdentity = currentOptions.pageIdentity || {
      pageName: '',
      subPageName: ''
    };
    const trackedElements = new WeakSet();
    const reportedMediaSignaturesByElement = new WeakMap();
    let exposureObserver = null;
    let domMutationObserver = null;
    let carouselExposureListenerBound = false;

    function isMediaExposureElement(element) {
      const tagName = element && element.tagName ? String(element.tagName).toLowerCase() : '';

      return tagName === 'img' || tagName === 'video';
    }

    function buildMediaExposureSignature(element) {
      const raw =
        (element && element.currentSrc) ||
        (element &&
          typeof element.getAttribute === 'function' &&
          (element.getAttribute('src') ||
            element.getAttribute('data-src') ||
            element.getAttribute('poster'))) ||
        '';

      return String(raw).trim();
    }

    function hasReportedMediaSignature(element, signature) {
      if (!signature) {
        return false;
      }

      const signatureSet = reportedMediaSignaturesByElement.get(element);

      return Boolean(signatureSet && signatureSet.has(signature));
    }

    function rememberMediaSignature(element, signature) {
      if (!signature) {
        return;
      }

      let signatureSet = reportedMediaSignaturesByElement.get(element);

      if (!signatureSet) {
        signatureSet = new Set();
        reportedMediaSignaturesByElement.set(element, signatureSet);
      }

      signatureSet.add(signature);
    }

    function isElementNode(node) {
      return Boolean(node && node.nodeType === 1);
    }

    function isTrackableElement(element, reportOptions) {
      if (!element || !element.isConnected) {
        return false;
      }

      const currentReportOptions = reportOptions || {};
      const mediaSource = element.currentSrc ||
        element.getAttribute('src') ||
        element.getAttribute('poster') ||
        element.getAttribute('data-src') ||
        '';

      if (isMediaExposureElement(element)) {
        if (
          !currentReportOptions.forceMediaExposure &&
          hasReportedMediaSignature(element, buildMediaExposureSignature(element))
        ) {
          return false;
        }

        if (
          element.hidden ||
          String(core.utils.fillEmptyValue(element.getAttribute('aria-hidden'))).toLowerCase() === 'true' ||
          element.disabled
        ) {
          return false;
        }

        return Boolean(mediaSource);
      }

      if (trackedElements.has(element)) {
        return false;
      }

      const computedStyle = typeof core.window.getComputedStyle === 'function'
        ? core.window.getComputedStyle(element)
        : null;

      return core.utils.isTrackableElementCandidate({
        tagName: element.tagName,
        href: element.getAttribute('href'),
        explicitType: element.dataset && element.dataset.trackType,
        hidden: element.hidden,
        ariaHidden: element.getAttribute('aria-hidden'),
        disabled: element.disabled,
        mediaSrc: mediaSource,
        allowMediaElements: true,
        display: computedStyle && computedStyle.display,
        visibility: computedStyle && computedStyle.visibility
      });
    }

    function isFullyVisible(element) {
      const rect = typeof element.getBoundingClientRect === 'function'
        ? element.getBoundingClientRect()
        : null;
      const viewportWidth = core.window.innerWidth || 0;
      const viewportHeight = core.window.innerHeight || 0;

      if (!rect || rect.width <= 0 || rect.height <= 0) {
        return false;
      }

      return rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= viewportHeight &&
        rect.right <= viewportWidth;
    }

    function buildSharedElementTrackingFields(element) {
      const metadata = core.utils.collectElementAncestorMetadata(element, core.document.body);

      return core.utils.buildElementTrackingData({
        pageIdentity: pageIdentity,
        trackingContext: core.trackingContext,
        elementInfo: {
          explicitName: element.dataset && element.dataset.trackName,
          explicitId: (element.dataset && element.dataset.trackId) || element.id,
          explicitType: element.dataset && element.dataset.trackType,
          explicitPosition: element.dataset && element.dataset.trackPosition,
          ariaLabel: element.getAttribute('aria-label'),
          title: element.getAttribute('title'),
          altText: element.getAttribute('alt'),
          textContent: element.textContent,
          hrefToken: core.utils.getElementHrefToken(
            element,
            core.window.location && core.window.location.origin ? core.window.location.origin : ''
          ),
          mediaToken: core.utils.getElementMediaToken(
            element,
            core.window.location && core.window.location.origin ? core.window.location.origin : ''
          ),
          tagName: element.tagName,
          role: element.getAttribute('role'),
          ancestorTags: metadata.ancestorTags,
          ancestorClassNames: metadata.ancestorClassNames,
          srcUrl: element.getAttribute('src')
        }
      });
    }

    function buildElementExposureData(element, now) {
      const sharedFields = buildSharedElementTrackingFields(element);
      const pageTrackingContext = core.window && core.window.pageTrackingContext;
      const popupName = core.utils.findPopupNameForClickContext
        ? core.utils.findPopupNameForClickContext(element, core.document.body)
        : '';

      return core.buildDataInfo({
        type: core.config.eventTypes && core.config.eventTypes.elementExposure,
        pageName: sharedFields.pageName,
        subPageName: sharedFields.subPageName,
        elementName: sharedFields.elementName,
        elementType: sharedFields.elementType,
        productName: pageTrackingContext.productTitle || sharedFields.productName,
        productId: pageTrackingContext.productId,
        position: sharedFields.position,
        popupName: core.utils.fillEmptyValue(popupName)
      }, now);
    }

    function reportElementExposure(element, sessionModule, sessionCallbacks, reportOptions) {
      const nowMs = Date.now();
      const isMedia = isMediaExposureElement(element);
      const currentReportOptions = reportOptions || {};
      let mediaSignature = '';

      if (isMedia) {
        mediaSignature = buildMediaExposureSignature(element);

        if (
          !mediaSignature ||
          (!currentReportOptions.forceMediaExposure && hasReportedMediaSignature(element, mediaSignature))
        ) {
          return false;
        }
      } else if (trackedElements.has(element)) {
        return false;
      }

      const dataInfo = buildElementExposureData(element, new Date(nowMs));

      if (!dataInfo.type || !dataInfo.elementName || !dataInfo.elementType) {
        return false;
      }

      if (isMedia) {
        rememberMediaSignature(element, mediaSignature);
      } else {
        trackedElements.add(element);
      }

      if (sessionModule && typeof sessionModule.ensureActiveSession === 'function') {
        sessionModule.ensureActiveSession(nowMs, sessionCallbacks || {});
      }

      core.markActivity(nowMs);

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: dataInfo
      }, true);
    }

    function observeElement(element) {
      if (!exposureObserver || !isTrackableElement(element)) {
        return;
      }

      exposureObserver.observe(element);
    }

    function reportVisibleMediaElements(rootNode, sessionModule, sessionCallbacks, reportOptions) {
      let reported = false;
      const currentReportOptions = reportOptions || {};

      if (!rootNode) {
        return false;
      }

      function reportIfVisible(element) {
        if (
          !isMediaExposureElement(element) ||
          !isTrackableElement(element, currentReportOptions) ||
          !isFullyVisible(element)
        ) {
          return;
        }

        reported = reportElementExposure(element, sessionModule, sessionCallbacks, currentReportOptions) || reported;
      }

      if (isElementNode(rootNode)) {
        reportIfVisible(rootNode);
      }

      if (typeof rootNode.querySelectorAll === 'function') {
        rootNode.querySelectorAll('img, video').forEach(reportIfVisible);
      }

      return reported;
    }

    function scheduleVisibleMediaCheck(rootNode, sessionModule, sessionCallbacks, reportOptions) {
      function runCheck() {
        reportVisibleMediaElements(rootNode, sessionModule, sessionCallbacks, reportOptions);
      }

      if (typeof core.window.requestAnimationFrame === 'function') {
        core.window.requestAnimationFrame(function scheduleSecondFrame() {
          core.window.requestAnimationFrame(runCheck);
        });
        return;
      }

      core.window.setTimeout(runCheck, 0);
    }

    function scanTrackableElements(rootNode) {
      if (!rootNode || !exposureObserver) {
        return;
      }

      if (isElementNode(rootNode) && rootNode.matches('a[href], button, img, video, [data-track-type]')) {
        observeElement(rootNode);
      }

      if (typeof rootNode.querySelectorAll === 'function') {
        rootNode.querySelectorAll('a[href], button, img, video').forEach(function observeMatchedElement(element) {
          observeElement(element);
        });
        rootNode.querySelectorAll('[data-track-type]').forEach(function observeTypedElement(element) {
          observeElement(element);
        });
      }
    }

    function bindMutationObserver(sessionModule, sessionCallbacks) {
      if (typeof core.window.MutationObserver !== 'function' || !core.document.body) {
        return;
      }

      domMutationObserver = new core.window.MutationObserver(function handleMutations(mutations) {
        mutations.forEach(function handleMutation(mutation) {
          if (mutation.type === 'attributes' && isElementNode(mutation.target)) {
            const targetElement = mutation.target;

            scanTrackableElements(targetElement);
            scheduleVisibleMediaCheck(targetElement, sessionModule, sessionCallbacks);

            return;
          }

          Array.prototype.forEach.call(mutation.addedNodes || [], function handleAddedNode(node) {
            if (!isElementNode(node)) {
              return;
            }

            scanTrackableElements(node);
          });
        });
      });

      domMutationObserver.observe(core.document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'poster', 'class', 'style', 'selected-index', 'aria-current']
      });
    }

    function bindCarouselExposureEvents(sessionModule, sessionCallbacks) {
      if (carouselExposureListenerBound || !core.document || typeof core.document.addEventListener !== 'function') {
        return;
      }

      core.document.addEventListener('slider:change', function handleSliderChange(event) {
        const eventDetail = event && event.detail ? event.detail : {};
        const targetElement = eventDetail.currentElement || (event && event.target ? event.target : core.document.body);

        scheduleVisibleMediaCheck(targetElement, sessionModule, sessionCallbacks, {
          forceMediaExposure: eventDetail.forceMediaExposure === true
        });
      });
      carouselExposureListenerBound = true;
    }

    function startObserving(sessionModule, sessionCallbacks) {
      if (
        !core.canTrack() ||
        !core.config.eventTypes ||
        !core.config.eventTypes.elementExposure ||
        typeof core.window.IntersectionObserver !== 'function'
      ) {
        return false;
      }

      exposureObserver = new core.window.IntersectionObserver(
        function handleIntersections(entries) {
          entries.forEach(function handleEntry(entry) {
            const targetElement = entry.target;

            if (!entry.isIntersecting || entry.intersectionRatio < 0.99 || !isFullyVisible(targetElement)) {
              return;
            }

            if (!isMediaExposureElement(targetElement)) {
              exposureObserver.unobserve(targetElement);
            }

            reportElementExposure(targetElement, sessionModule, sessionCallbacks);
          });
        },
        {
          threshold: [0.99, 1]
        }
      );

      scanTrackableElements(core.document);
      bindMutationObserver(sessionModule, sessionCallbacks);
      bindCarouselExposureEvents(sessionModule, sessionCallbacks);
      return true;
    }

    function scheduleObservation(sessionModule, sessionCallbacks) {
      function start() {
        startObserving(sessionModule, sessionCallbacks);
      }

      function deferStart() {
        if (typeof core.window.requestAnimationFrame === 'function') {
          core.window.requestAnimationFrame(function scheduleSecondFrame() {
            core.window.requestAnimationFrame(start);
          });
        } else {
          core.window.setTimeout(start, 0);
        }
      }

      if (core.document.readyState === 'complete' || core.document.readyState === 'interactive') {
        deferStart();
        return;
      }

      core.document.addEventListener('readystatechange', function handleReadyStateChange() {
        if (core.document.readyState === 'loading') {
          return;
        }

        core.document.removeEventListener('readystatechange', handleReadyStateChange);
        deferStart();
      });
    }

    return {
      isTrackableElement,
      isFullyVisible,
      buildSharedElementTrackingFields,
      buildElementExposureData,
      reportElementExposure,
      observeElement,
      reportVisibleMediaElements,
      scheduleVisibleMediaCheck,
      scanTrackableElements,
      bindMutationObserver,
      bindCarouselExposureEvents,
      startObserving,
      scheduleObservation
    };
  };
});
